import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { HistoryTables } from '../../shared'
import { ErrorResponseSchema } from '../../shared/types'
import {
  FilterAnimalIdsByStatusBodySchema,
  FilterAnimalIdsByStatusResponseSchema,
  BackfillFeedingPlanHistoryRequestSchema,
  BackfillStarterBoxFeedingPlansRequestSchema,
  BackfillStarterBoxFeedingPlansResponseSchema,
  StarterBoxBackfillSubscriptionQuerySchema,
  StarterBoxBackfillSubscriptionResponseSchema,
  BackfillFeedingPlanHistoryResponseSchema,
  CancelSubscriptionExtraBodySchema,
  CancelSubscriptionSchema,
  CreateSubscriptionBodySchema,
  CreateSubscriptionDiscountBodySchema,
  CreateSubscriptionDiscountResponseSchema,
  CurrentSubscriptionSearchBodySchema,
  CurrentSubscriptionSearchResponseSchema,
  DeleteMigratedSubscriptionDiscountQueryParamsSchema,
  DeleteSubscriptionDiscountBodySchema,
  DeleteSubscriptionPathParams,
  ExtraSchema,
  FeedingPlanSchema,
  GetSubscriptionQuerySchema,
  GetSubscriptionHistoryResponseSchema,
  GetSubscriptionsByUserQuerySchema,
  ListSubscriptionsQuerySchema,
  ListSubscriptionsResponseSchema,
  MigrateSubscriptionBodySchema,
  MigrateSubscriptionHistoryDataRequestSchema,
  MigrateSubscriptionHistoryResponseSchema,
  PauseSubscriptionBodySchema,
  SearchSubscriptionsByIdsBodySchema,
  SearchSubscriptionsByIdsResponseSchema,
  SearchSubscriptionsQuerySchema,
  SubscriptionSchema,
  SubscriptionSuccessResponseSchema,
  UpdateSubscriptionBodySchema,
  UpdateSubscriptionLastProcessedOrderIndexBodySchema,
  UpdateSubscriptionLastProcessedOrderIndexResponseSchema,
  VerifyMigratedHistoricalDataRequestSchema,
  VerifyMigratedSubscriptionResponseSchema,
} from './schemas'

const c = initContract()

const options = {
  pathPrefix: '/subscriptions',
  commonResponses: {
    400: ErrorResponseSchema,
    404: ErrorResponseSchema,
    409: ErrorResponseSchema,
    422: ErrorResponseSchema,
    500: ErrorResponseSchema,
    503: ErrorResponseSchema,
  },
}

// Routes are split into groups to avoid TS7056 (inferred type exceeds maximum serialisable length)
const subscriptionRoutes = c.router({
  getSubscriptionsByUser: {
    method: 'GET',
    path: '',
    query: GetSubscriptionsByUserQuerySchema,
    responses: {
      200: SubscriptionSchema.array(),
    },
  },
  searchSubscriptions: {
    method: 'GET',
    path: '/search',
    query: SearchSubscriptionsQuerySchema,
    responses: {
      200: SubscriptionSchema.array(),
    },
  },
  searchSubscriptionsByIds: {
    method: 'POST',
    path: '/search',
    body: SearchSubscriptionsByIdsBodySchema,
    responses: {
      200: SearchSubscriptionsByIdsResponseSchema,
    },
  },
  currentSubscriptionSearch: {
    method: 'POST',
    description: 'Batch lookup of the current (latest by createdAt) subscription for each supplied animalId and/or userId. Returns one entry per requested id, in request order, with foundSubscription null when the id has no subscription.',
    path: '/current-subscription-search',
    body: CurrentSubscriptionSearchBodySchema,
    responses: {
      200: CurrentSubscriptionSearchResponseSchema,
    },
  },
  filterAnimalIdsByStatus: {
    method: 'POST',
    description: 'Filters the supplied animalIds to those on a subscription for the given brand matching the given subscription and feeding plan statuses.',
    path: '/filter-animal-ids-by-status',
    body: FilterAnimalIdsByStatusBodySchema,
    responses: {
      200: FilterAnimalIdsByStatusResponseSchema,
    },
  },
  listSubscriptions: {
    method: 'GET',
    path: '/list',
    query: ListSubscriptionsQuerySchema,
    responses: {
      200: ListSubscriptionsResponseSchema,
    },
  },
  createSubscription: {
    method: 'POST',
    path: '',
    body: CreateSubscriptionBodySchema,
    responses: {
      201: SubscriptionSchema,
    },
  },
  getSubscription: {
    method: 'GET',
    path: '/:id',
    pathParams: SubscriptionSchema.pick({ id: true }),
    query: GetSubscriptionQuerySchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  getSubscriptionHistory: {
    method: 'GET',
    path: '/:id/history',
    pathParams: SubscriptionSchema.pick({ id: true }),
    summary: 'List subscriptions_history snapshot rows (id, createdAt, version) for a subscription, oldest first',
    responses: {
      200: GetSubscriptionHistoryResponseSchema,
    },
  },
  updateSubscription: {
    method: 'PATCH',
    path: '/:id',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: UpdateSubscriptionBodySchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  updateSubscriptionLastProcessedOrderIndex: {
    method: 'PATCH',
    description: 'Writes lastProcessedOrderIndex in one atomic statement. Takes no version and runs no optimistic-lock check, so it cannot conflict with a concurrent subscription edit. Defaults to max(stored, orderIndex) and increments the subscription version; pass enforce to store orderIndex as given (a downwards correction), and skipVersionUpdate to leave the version untouched.',
    path: '/:id/last-processed-order-index',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: UpdateSubscriptionLastProcessedOrderIndexBodySchema,
    responses: {
      200: UpdateSubscriptionLastProcessedOrderIndexResponseSchema,
    },
  },
  pauseSubscription: {
    method: 'POST',
    path: '/:id/pause',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: PauseSubscriptionBodySchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  resumeSubscription: {
    method: 'POST',
    path: '/:id/resume',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  cancelSubscription: {
    method: 'POST',
    path: '/:id/cancel',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: CancelSubscriptionSchema,
    responses: {
      200: SubscriptionSchema,
    },
  },
  reactivateSubscription: {
    method: 'POST',
    path: '/reactivate/:userId',
    pathParams: SubscriptionSchema.pick({ userId: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  removeSubscription: {
    method: 'DELETE',
    path: '/:id',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  pauseSubscriptionFeedingPlan: {
    method: 'POST',
    path: '/:id/feeding-plan/:feedingPlanId/pause',
    pathParams: SubscriptionSchema.pick({ id: true }).extend({
      feedingPlanId: z.string().uuid(),
    }),
    body: null,
    responses: {
      200: FeedingPlanSchema,
    },
  },
  activateSubscriptionFeedingPlan: {
    method: 'POST',
    path: '/:id/feeding-plan/:feedingPlanId/active',
    pathParams: SubscriptionSchema.pick({ id: true }).extend({
      feedingPlanId: z.string().uuid(),
    }),
    body: null,
    responses: {
      200: FeedingPlanSchema,
    },
  },
  cancelSubscriptionExtra: {
    method: 'POST',
    path: '/:id/extras/:extraId/cancel',
    pathParams: SubscriptionSchema.pick({ id: true }).extend({
      extraId: z.string().uuid(),
    }),
    body: CancelSubscriptionExtraBodySchema,
    responses: {
      200: ExtraSchema,
    },
  },
  createSubscriptionDiscount: {
    method: 'POST',
    description: 'Create a new subscription discount',
    path: '/:id/discounts',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: CreateSubscriptionDiscountBodySchema,
    responses: {
      201: CreateSubscriptionDiscountResponseSchema,
    },
  },
  deleteAllSubscriptionDiscounts: {
    method: 'DELETE',
    description: 'Soft-delete all subscription discounts',
    path: '/:id/discounts',
    pathParams: SubscriptionSchema.pick({ id: true }),
    query: DeleteMigratedSubscriptionDiscountQueryParamsSchema,
    body: DeleteSubscriptionDiscountBodySchema,
    responses: {
      204: z.undefined(),
    },
  },
  deleteSubscriptionDiscount: {
    method: 'DELETE',
    description: 'Soft-delete specific subscription discount',
    path: '/:subscriptionId/discounts/:subscriptionDiscountId',
    pathParams: DeleteSubscriptionPathParams,
    body: DeleteSubscriptionDiscountBodySchema,
    responses: {
      204: z.undefined(),
    },
  },
}, options)

const subscriptionMigrationRoutes = c.router({
  migrateSubscriptions: {
    method: 'POST',
    description: 'This endpoint will only used by migration service to migrate data from laravel to phoenix',
    path: '/migration',
    body: z.object({
      dryRun: z.boolean().default(false),
      userId: z.string().uuid(),
      subscriptions: MigrateSubscriptionBodySchema.array(),
    }),
    responses: {
      200: SubscriptionSchema.array(),
    },
  },
  deleteMigratedSubscription: {
    method: 'DELETE',
    description: 'This endpoint will only used by migration service to delete the migrated data from laravel to phoenix',
    path: '/migration/:userId',
    pathParams: SubscriptionSchema.pick({ userId: true }),
    responses: {
      200: SubscriptionSuccessResponseSchema,
    },
  },
  verifyMigratedSubscriptions: {
    method: 'POST',
    description: 'Thie endpoint will be used to verify the migrated subscription',
    path: '/migrations/verify',
    body: z.object({
      dryRun: z.boolean().default(false),
      userId: z.string().uuid(),
      subscriptions: MigrateSubscriptionBodySchema.array(),
    }),
    responses: {
      200: VerifyMigratedSubscriptionResponseSchema,
    },
  },
  migrateHistoricalData: {
    method: 'POST',
    description: 'This endpoint will only used by migration service to migrate the historical data from laravel to phoenix for the existing subscriptions in phoenix',
    path: '/migration/history',
    body: MigrateSubscriptionHistoryDataRequestSchema,
    responses: {
      200: MigrateSubscriptionHistoryResponseSchema,
    },
  },
  verifyMigratedHistoricalData: {
    method: 'POST',
    description: 'This endpoint will be used by the migration to verify the migrated historical data for the subscription',
    path: '/migration/history/verify',
    body: VerifyMigratedHistoricalDataRequestSchema,
    responses: {
      200: VerifyMigratedSubscriptionResponseSchema,
    },
  },
  backfillFeedingPlanHistoryIds: {
    method: 'POST',
    description: 'Repairs one batch of the self-referencing feeding-plan history ids left by the pre-PR-#3631 migration. Driven by the backfillFeedingPlanHistoryIds workflow — not intended to be called by hand.',
    path: '/migration/history/feeding-plan-backfill',
    body: BackfillFeedingPlanHistoryRequestSchema,
    responses: {
      200: BackfillFeedingPlanHistoryResponseSchema,
    },
  },
  getStarterBoxBackfillSubscriptions: {
    method: 'GET',
    description: 'Provides subscription data for starter box backfill',
    path: '/migration/starter-box-backfill/candidates',
    query: StarterBoxBackfillSubscriptionQuerySchema,
    responses: {
      200: StarterBoxBackfillSubscriptionResponseSchema,
    },
  },
  backfillStarterBoxFeedingPlans: {
    method: 'POST',
    description: 'Adds starter box feeding plan to the feeding plans table',
    path: '/migration/starter-box-backfill',
    body: BackfillStarterBoxFeedingPlansRequestSchema,
    responses: {
      200: BackfillStarterBoxFeedingPlansResponseSchema,
    },
  },
  revertMigratedHistoricalData: {
    method: 'DELETE',
    description: 'This endpoint will only used by migration service to revert the migrated historical data from laravel to phoenix in case there is any issue after migration',
    path: '/migration/history/revert/:userId',
    pathParams: SubscriptionSchema.pick({ userId: true }),
    body: z.object({
      selectedHistoryTables: z.nativeEnum(HistoryTables).array().optional().describe('Subset of history tables to migrate. Defaults to all implemented tables.'),
    }).optional(),
    responses: {
      200: SubscriptionSuccessResponseSchema,
    },
  },
}, options)

const subscriptionRevertRoutes = c.router({
  revertResumeSubscription: {
    method: 'POST',
    path: '/:id/resume/revert',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
  revertReactivateSubscription: {
    method: 'POST',
    path: '/:id/reactivate/revert',
    pathParams: SubscriptionSchema.pick({ id: true }),
    body: null,
    responses: {
      200: SubscriptionSchema,
    },
  },
}, options)

type SubscriptionServiceContract = typeof subscriptionRoutes
  & typeof subscriptionMigrationRoutes
  & typeof subscriptionRevertRoutes

export const subscriptionServiceContract: SubscriptionServiceContract = {
  ...subscriptionRoutes,
  ...subscriptionMigrationRoutes,
  ...subscriptionRevertRoutes,
}
